import { useEffect, useState, useRef, FC } from 'react';
import { css } from '@emotion/react';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { CustomizationFlagQueries } from '@frontend/api-customization-flags';
import { PersonAPI, PersonQueries, PersonHelpers } from '@frontend/api-person';
import { CallIntelPatientInsightsProvider } from '@frontend/call-intel-insights';
import { ContactHistoryProvider, HistoryEmptyStateGraphic, HistoryPanel } from '@frontend/contact-history-panel';
import { useTranslation } from '@frontend/i18n';
import { InsuranceTab } from '@frontend/insurance-tab';
import { useHasWIVOrEIVAccess } from '@frontend/insurance-verification';
import { usePersonLocations } from '@frontend/person-invoice-create';
import { CallPopInterface, CallPopWidget, useCallPopStateSync } from '@frontend/pop';
import { useAppScopeStore, useScopedQuery } from '@frontend/scope';
import { ContactPanelTabsType, useContactPanelShallowStore, useContactPanelStore } from '@frontend/shared';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { SurfaceHeader } from '@frontend/surface-header';
import { useGetTaskCenterCustomizationFlagValue } from '@frontend/task-center-scope';
import { pendo, sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import {
  Tabs,
  SkeletonLoaders,
  TextButton,
  BackIcon,
  Text,
  NotificationBadge,
  useAlert,
} from '@frontend/design-system';
import { CustomerDetails } from './contact-details-sections/customer-details';
import { ContactPanelHeader } from './contact-panel-header/contact-panel-header';
import { ContactTaskTabContent } from './contact-tasks-tab-content';
import { FormsPanel } from './forms-panel/forms-panel';
import { useGetPersonSourceType } from './hooks/use-get-person-source-type';
import { HouseholdNotes } from './household-notes';
import { HouseholdTasks } from './household-tasks';
import { PaymentsPanel } from './payments-panel';
import { ContactPanelComponentType, ContactPanelProps } from './types';

export const ContactPanel = ({
  Component,
  onClose,
}: {
  Component: ContactPanelComponentType;
  onClose?: () => void;
}) => {
  const { context, setShow } = useSlidePanelShallowStore('context', 'setShow');
  const { personId, personLocationId: personLocationIdContext } = useContactPanelShallowStore(
    'personId',
    'resetPersonId',
    'personLocationId'
  );

  /**
   * TEMPORARY FIX
   * useSlidePanelShallowStore()->setPersonId() is often used without setting a location id.
   * So personLocationId will often be undefined. As when opening contact panel from requests table.
   *
   * So for now, get the locations that belong to the person. Get the first one and pass that down.
   * Still a bandaid fix because the contact panel needs only one location and there are multiple bugs
   * related to locations[0], because anything at locations[1] is inaccessible.
   */
  const { selectedLocationIds } = useAppScopeStore();
  const { personLocationIds } = usePersonLocations({ locationIds: selectedLocationIds, personId });

  /**
   * Sometimes personLocationIdContext is just plain wrong.
   * It comes from the avatar, which does locations[0]. So,
   * check if it's in the person's location IDs before using it,
   * when given.
   */
  const personLocationId =
    !!personLocationIdContext && personLocationIds.includes(personLocationIdContext)
      ? personLocationIdContext
      : personLocationIds?.[0] || selectedLocationIds?.[0];

  const { t } = useTranslation('contacts');
  const contextData = context as { backlink?: () => void } | undefined;
  const { setOutlet } = useCallPopStateSync();

  const { getPersonSourceType } = useGetPersonSourceType();

  const { data, isLoading, isError } = useScopedQuery({
    queryKey: [personId, { locationId: personLocationId }],
    queryFn: () => PersonAPI.getPersonExtended(personId, personLocationId),
    select: (personData) => {
      return {
        ...personData,
        SourceType: getPersonSourceType(personData) ?? personData.SourceType,
      };
    },
    retry: 1,
    onError: () => {
      sentry.error({
        error: 'Error getting person extended',
        topic: 'contacts',
        team: 'fe-messaging',
        severityLevel: 'error',
        addContext: {
          name: 'PersonID',
          context: {
            personId,
            personLocationId,
          },
        },
      });
    },
  });

  const onContactPanelClose = () => {
    useContactPanelStore.getState().resetSelectedTab();
    onClose?.();
  };

  if (isError) {
    return (
      <div
        css={{ margin: theme.spacing(2), height: '80%', button: { marginBottom: theme.spacing(2), float: 'right' } }}
      >
        <SurfaceHeader.Root noBorder css={{ margin: theme.spacing(-2, -2, 0, -2), width: 'calc(100% + 32px)' }}>
          <SurfaceHeader.Row>
            <SurfaceHeader.Title>{t('Oops...')}</SurfaceHeader.Title>
            <SurfaceHeader.Actions>
              <SurfaceHeader.Icon
                iconName='x'
                onClick={() => {
                  setShow(false);
                  setOutlet('queue');
                }}
              />
            </SurfaceHeader.Actions>
          </SurfaceHeader.Row>
          <SurfaceHeader.Body>{t(`This user doesn't exist`)}</SurfaceHeader.Body>
        </SurfaceHeader.Root>
        <div
          css={css`
            height: 90%;
            display: flex;
            justify-content: center;
          `}
        >
          <HistoryEmptyStateGraphic />
        </div>
      </div>
    );
  }
  return (
    <Component
      isLoading={isLoading}
      person={data}
      backlink={contextData?.backlink}
      personLocationId={personLocationId}
      onClose={onContactPanelClose}
    />
  );
};

ContactPanel.cleanup = () => {
  useContactPanelStore.getState().resetPersonId();
  useContactPanelStore.getState().resetSelectedTab();
  CallPopInterface.dismissTray();
};

export const ContactPanelTrayComponent: ContactPanelComponentType = ({
  person,
  isLoading,
  personLocationId,
  onClose,
}) => {
  if (!person) {
    return null;
  }

  return (
    <>
      <CallPopSection />
      <div
        style={{
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <SkeletonLoaders isLoading={isLoading}>
          <ContactPanelHeader personLocationID={personLocationId} person={person} onClose={onClose} />
        </SkeletonLoaders>
        <ContactPanelTabs person={person} isLoading={isLoading} personLocationId={personLocationId} />
      </div>
    </>
  );
};

export const ContactPanelComponent: ContactPanelComponentType = ({
  person,
  isLoading,
  backlink,
  personLocationId,
  onClose,
}) => {
  const { t } = useTranslation('contacts');
  const seenEventFiredRef = useRef(false);

  // Fire pendo event when component mounts and person exists
  useEffect(() => {
    if (person && !seenEventFiredRef.current) {
      pendo.track('contact_panel_seen', {
        personId: person.PersonID,
        personName: PersonHelpers.getFullName({
          FirstName: person?.FirstName,
          LastName: person?.LastName,
        }),
        timestamp: new Date().toISOString(),
      });
      seenEventFiredRef.current = true;
    }
  }, [person]);

  if (!person) {
    return null;
  }

  return (
    <>
      <CallPopSection />
      <div
        style={{
          position: 'relative',
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'grid',
            justifyContent: 'space-between',
            alignItems: 'center',
            gridTemplateAreas: '"backlink closeButton"',
            gridTemplateColumns: 'auto auto',
          }}
        >
          {backlink && (
            <TextButton css={{ gridArea: 'backlink' }} icon={BackIcon} onClick={backlink}>
              {t('Back')}
            </TextButton>
          )}
        </div>
        <SkeletonLoaders isLoading={isLoading}>
          <div
            css={css`
              position: relative;
            `}
          >
            <ContactPanelHeader onClose={onClose} personLocationID={personLocationId} person={person} />
          </div>
        </SkeletonLoaders>
        <ContactPanelTabs person={person} isLoading={isLoading} personLocationId={personLocationId} />
      </div>
    </>
  );
};

export const ContactPanelTabs: FC<ContactPanelProps> = ({
  person,
  isLoading,
  personLocationId,
  syncSelectedTabWithStore = true,
}) => {
  const { t } = useTranslation('contacts');
  const { selectedTab, setSelectedTab } = useContactPanelShallowStore('selectedTab', 'setSelectedTab');
  const [lastSelectedContactPanelTab, setLastSelectedContactPanelTab] = useState<ContactPanelTabsType>('overview-tab');
  const alert = useAlert();
  const { data: householdNotes } = PersonQueries.useGetHouseholdNotes(person?.WeaveHouseholdID || '', {
    onError: () => alert.error(t('Failed to load notes.')),
  });
  const { selectedLocationIds, accessibleLocationData } = useAppScopeStore();
  const { isTaskCenterEnabled } = useGetTaskCenterCustomizationFlagValue();

  const locationData = accessibleLocationData?.[selectedLocationIds?.[0]];

  const { getHasInsuranceVerificationAccessForLocation } = useHasWIVOrEIVAccess();
  const showInsurance = getHasInsuranceVerificationAccessForLocation(personLocationId || locationData?.id);

  const { isLoading: isLoadingFormFeature, isFeatureHiddenInAllLocations } =
    CustomizationFlagQueries.useAggregateCustomizationFlagDetails({
      locationIds: personLocationId ? [personLocationId] : selectedLocationIds,
      enabled: true,
      customizationFlag: Feature.FORMS,
    });

  const hasFormFeature = !isLoadingFormFeature && !isFeatureHiddenInAllLocations;

  // Set the last selected tab when the component mounts or when selectedTab changes
  useEffect(() => {
    if (syncSelectedTabWithStore && selectedTab) {
      setLastSelectedContactPanelTab(selectedTab);
    }
  }, [selectedTab, syncSelectedTabWithStore]);

  return (
    <Tabs
      controlledTab={lastSelectedContactPanelTab}
      onChange={(id) => {
        setLastSelectedContactPanelTab(id as ContactPanelTabsType);
        if (syncSelectedTabWithStore) {
          setSelectedTab(id as ContactPanelTabsType);
        }
      }}
    >
      <Tabs.Bar fullWidth>
        <Tabs.Tab id='overview-tab' trackingId='contact-2.0-tray-tab-overview'>
          {t('Overview')}
        </Tabs.Tab>
        <Tabs.Tab id='payments-tab' trackingId='contact-2.0-tray-tab-payments'>
          {t('Payments')}
        </Tabs.Tab>
        {showInsurance && (
          <Tabs.Tab id='insurance-tab' trackingId='contact-2.0-tray-tab-insurance'>
            {t('Insurance')}
          </Tabs.Tab>
        )}
        {hasFormFeature && (
          <Tabs.Tab id='forms-tab' trackingId='contact-2.0-tray-tab-forms'>
            {t('Forms')}
          </Tabs.Tab>
        )}
        <Tabs.Tab id='notes-tab' trackingId='contact-2.0-tray-tab-notes'>
          <Text
            as={'span'}
            css={css`
              display: flex;
              align-items: center;
              gap: ${theme.spacing(1)};
              font-size: ${theme.fontSize(14)};
            `}
          >
            {t('Notes')}
            {!!householdNotes?.length && (
              <NotificationBadge css={{ background: theme.colors.primary50 }}>
                {householdNotes?.length}
              </NotificationBadge>
            )}
          </Text>
        </Tabs.Tab>
        {isTaskCenterEnabled && (
          <Tabs.Tab id='tasks-tab' trackingId='contact-2.0-tray-tab-tasks'>
            {t('Tasks')}
          </Tabs.Tab>
        )}
        <Tabs.Tab id='history-tab' trackingId='contact-2.0-tray-tab-history'>
          {t('History')}
        </Tabs.Tab>
      </Tabs.Bar>
      <ContactHistoryProvider>
        <CallIntelPatientInsightsProvider personId={person?.PersonID ?? ''}>
          <Tabs.Panel
            controller='overview-tab'
            css={css`
              overflow-y: auto;
              height: 100%;
              background-color: ${theme.colors.neutral5};
              padding: ${theme.spacing(2)};
            `}
          >
            {lastSelectedContactPanelTab === 'overview-tab' && (
              <SkeletonLoaders isLoading={isLoading}>
                <CustomerDetails
                  data={person}
                  locationId={personLocationId}
                  onTabChange={setLastSelectedContactPanelTab}
                />
              </SkeletonLoaders>
            )}
          </Tabs.Panel>
          <Tabs.Panel
            controller='insurance-tab'
            css={css`
              height: 100%;
              background-color: ${theme.colors.neutral5};
              padding: ${theme.spacing(3)};
              overflow-y: auto;
            `}
          >
            {lastSelectedContactPanelTab === 'insurance-tab' && (
              <SkeletonLoaders isLoading={isLoading}>
                <InsuranceTab locationId={(personLocationId as string) || locationData.id} personInfo={person} />
              </SkeletonLoaders>
            )}
          </Tabs.Panel>
          <Tabs.Panel
            controller='forms-tab'
            css={css`
              height: 100%;
              background-color: ${theme.colors.neutral5};
              padding: ${theme.spacing(3)};
            `}
          >
            {lastSelectedContactPanelTab === 'forms-tab' && (
              <SkeletonLoaders isLoading={isLoading}>
                <FormsPanel person={person} locationId={personLocationId} />
              </SkeletonLoaders>
            )}
          </Tabs.Panel>
          <Tabs.Panel
            controller='payments-tab'
            css={css`
              overflow-y: auto;
              height: 100%;
            `}
          >
            <PaymentsPanel
              locationId={personLocationId}
              isActive={lastSelectedContactPanelTab === 'payments-tab'}
              trackingId='contact-tray-payments'
              person={person}
              css={css`
                min-height: 100%;
              `}
            />
          </Tabs.Panel>
          <Tabs.Panel
            controller='notes-tab'
            css={css`
              overflow-y: auto;
              height: 100%;
            `}
          >
            {lastSelectedContactPanelTab === 'notes-tab' && (
              <HouseholdNotes householdId={person?.WeaveHouseholdID || ''} />
            )}
          </Tabs.Panel>
          <Tabs.Panel
            controller='tasks-tab'
            css={css`
              overflow-y: auto;
              height: 100%;
            `}
          >
            {lastSelectedContactPanelTab === 'tasks-tab' && (
              <>
                {isTaskCenterEnabled ? (
                  <ContactTaskTabContent contactId={person?.PersonID || ''} />
                ) : (
                  <HouseholdTasks
                    householdId={person?.WeaveHouseholdID || ''}
                    sourceId={person?.SourceID || ''}
                    clientLocationId={person?.ClientLocationID || ''}
                  />
                )}
              </>
            )}
          </Tabs.Panel>
          <Tabs.Panel controller='history-tab' css={{ overflowY: 'auto' }}>
            {lastSelectedContactPanelTab === 'history-tab' && <HistoryPanel personId={person?.PersonID || ''} />}
          </Tabs.Panel>
        </CallIntelPatientInsightsProvider>
      </ContactHistoryProvider>
    </Tabs>
  );
};

const CallPopSection = () => {
  const { outlet, notifications } = useCallPopStateSync();

  if (outlet !== 'profile' || notifications.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        background: theme.colors.neutral5,
        padding: theme.spacing(1),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <CallPopWidget notifications={notifications} />
    </div>
  );
};
